/**
 * Notifications API client (B15, CF-2400..2404).
 *
 * Mirrors friendsApi.ts: shared Envelope<T> + CSRF header injection + unwrap.
 */

import { useAuthStore } from '../store/useAuthStore';
import type { NotificationDto } from '@shared/notification.js';

interface Envelope<T> {
  sessionState: string;
  success: boolean;
  data: T | null;
  messages: Array<{ severity: string; message: string; details?: Record<string, unknown> }>;
}

export class NotificationsApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

function buildHeaders(includeCsrf = true): Headers {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  if (includeCsrf) {
    const csrf = useAuthStore.getState().csrfToken;
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }
  return headers;
}

async function unwrap<T>(res: Response): Promise<T> {
  const envelope = (await res.json()) as Envelope<T>;
  if (!res.ok || envelope.success === false || envelope.data === null) {
    const msg = envelope.messages?.[0];
    const code = (msg?.details?.['code'] as string | undefined) ?? 'unknown';
    throw new NotificationsApiError(res.status, code, msg?.message ?? `HTTP ${res.status}`);
  }
  return envelope.data;
}

export interface NotificationPrefEntry {
  category: string;
  sticky: boolean;
  inapp: boolean;
  sound: boolean;
  push: boolean;
}

export async function listNotifications(): Promise<NotificationDto[]> {
  const res = await fetch('/api/v1/notifications', {
    method: 'GET',
    headers: buildHeaders(false),
    credentials: 'include',
  });
  const data = await unwrap<{ notifications: NotificationDto[] }>(res);
  return data.notifications;
}

export async function getUnreadCount(): Promise<number> {
  const res = await fetch('/api/v1/notifications/unread-count', {
    method: 'GET',
    headers: buildHeaders(false),
    credentials: 'include',
  });
  const data = await unwrap<{ unread_count: number }>(res);
  return data.unread_count;
}

export async function markNotificationRead(id: number): Promise<void> {
  const res = await fetch(`/api/v1/notifications/${id}/read`, {
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
  });
  await unwrap(res);
}

export async function markAllNotificationsRead(): Promise<void> {
  const res = await fetch('/api/v1/notifications/mark-all-read', {
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
  });
  await unwrap(res);
}

export async function getNotificationPrefs(): Promise<Record<string, NotificationPrefEntry>> {
  const res = await fetch('/api/v1/notifications/prefs', {
    method: 'GET',
    headers: buildHeaders(false),
    credentials: 'include',
  });
  const data = await unwrap<{ prefs: Record<string, NotificationPrefEntry> }>(res);
  return data.prefs;
}

export async function updateNotificationPrefs(
  prefs: Record<string, { inapp?: boolean; sound?: boolean; push?: boolean }>,
): Promise<void> {
  const res = await fetch('/api/v1/notifications/prefs', {
    method: 'PUT',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify({ prefs }),
  });
  await unwrap(res);
}

export async function getVapidPublicKey(): Promise<string> {
  const res = await fetch('/api/v1/notifications/vapid-public-key', {
    method: 'GET',
    headers: buildHeaders(false),
    credentials: 'include',
  });
  const data = await unwrap<{ public_key: string }>(res);
  return data.public_key;
}

export async function pushSubscribe(sub: PushSubscriptionJSON): Promise<void> {
  const res = await fetch('/api/v1/notifications/push/subscribe', {
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      endpoint: sub.endpoint,
      keys: sub.keys,
    }),
  });
  await unwrap(res);
}

export async function pushUnsubscribe(endpoint: string): Promise<void> {
  const res = await fetch('/api/v1/notifications/push/unsubscribe', {
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify({ endpoint }),
  });
  await unwrap(res);
}
